/**
 * Sidebar — navegação principal.  Itens aparecem condicionalmente
 * baseado em role do user atual.
 */

import { NavLink } from "react-router-dom";
import {
  BellRing,
  Boxes,
  Building2,
  ChevronLeft,
  ChevronRight,
  Cpu,
  FileText,
  LayoutDashboard,
  MapPin,
  Settings,
  Gauge,
  Users,
} from "lucide-react";
import clsx from "clsx";

import { useAuth } from "@/contexts/AuthContext";
import type { Role } from "@/types";
import { BrandLockup, BrandSymbol } from "./Brand";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Mostra item apenas se user tem >= esse role. Sem prop = todos. */
  minRole?: Role;
  /** Mostra apenas pra esses roles exatos (sobrepõe minRole). */
  exactRoles?: Role[];
  /** Indica que é uma rota "exata" pro NavLink end. */
  end?: boolean;
}

function buildSections(tenantId: string | null | undefined): { title?: string; items: NavItem[] }[] {
  // "Empresa" só aparece pra tenant admin (super_admin acessa via lista em "Tenants")
  const empresaItem: NavItem | null = tenantId
    ? { to: `/admin/tenants/${tenantId}/edit`, label: "Empresa", icon: Building2, exactRoles: ["admin"] }
    : null;

  return [
    {
      items: [
        { to: "/",            label: "Dashboard",  icon: LayoutDashboard, end: true },
        // "Ativos" e não "Unidades": a frota não é só fria. Estufa, banho-maria e
        // balcão quente são o mesmo objeto para o sistema, e um nome que só
        // descreve metade dela envelhece no primeiro cliente de fora do frio.
        // Pelo mesmo motivo o ícone deixou de ser um floco de neve.
        { to: "/equipments",  label: "Ativos",     icon: Gauge },
        // Histórico de alerta era admin-only. Quem recebe o push de
        // madrugada não é necessariamente admin, e precisava de um lugar
        // pra conferir o que aconteceu.
        { to: "/ocorrencias", label: "Ocorrências", icon: BellRing },
        { to: "/reports",     label: "Relatórios", icon: FileText },
      ],
    },
    {
      title: "Administração",
      items: [
        ...(empresaItem ? [empresaItem] : []),
        { to: "/admin/sites",      label: "Sites",      icon: MapPin,    minRole: "admin" },
        { to: "/admin/equipments", label: "Cadastro de ativos", icon: Boxes, minRole: "admin" },
        { to: "/admin/devices",    label: "Devices",    icon: Cpu,       minRole: "admin" },
        { to: "/admin/users",      label: "Usuários",   icon: Users,     minRole: "admin" },
        // "Gerenciar alertas" e não "Alertas": aqui se CONFIGURA o que vigiar.
        // Quem clicava em "Alertas" esperava ver os alertas que dispararam —
        // isso é "Ocorrências", logo acima.
        { to: "/admin/alerts",     label: "Gerenciar alertas", icon: BellRing,  minRole: "admin" },
        { to: "/admin/settings",   label: "Thresholds", icon: Settings,  minRole: "admin" },
      ],
    },
    {
      title: "envpulse Staff",
      items: [
        { to: "/admin/tenants",    label: "Tenants",    icon: Building2, exactRoles: ["super_admin"] },
      ],
    },
  ];
}

interface SidebarProps {
  onNavigate?: () => void;
  /** Modo compacto — só ícones, labels viram tooltips. Default false. */
  collapsed?: boolean;
  /** Toggle collapse — se omitido, o botão não aparece (modo mobile drawer). */
  onToggleCollapsed?: () => void;
}

export function Sidebar({ onNavigate, collapsed = false, onToggleCollapsed }: SidebarProps) {
  const auth = useAuth();

  const sections = buildSections(auth.user?.tenant_id);
  const visibleSections = sections.map((sec) => ({
    ...sec,
    items: sec.items.filter((item) => {
      if (item.exactRoles) return auth.hasRole(...item.exactRoles);
      if (item.minRole) return auth.hasMinRole(item.minRole);
      return true;
    }),
  })).filter((sec) => sec.items.length > 0);

  return (
    <div className="flex h-full flex-col bg-[#0a0f1a] text-slate-200">
      {/* Brand — clicável, porque logo que não leva pra casa é decoração e
          todo mundo tenta clicar nele assim mesmo. */}
      <NavLink
        to="/"
        aria-label="Ir para o painel"
        className={clsx(
          "flex h-16 items-center border-b border-white/10 transition-colors hover:bg-white/5",
          collapsed ? "justify-center px-2" : "px-5",
        )}
      >
        {collapsed ? (
          <BrandSymbol className="h-8 w-8" />
        ) : (
          <BrandLockup size="md" withTagline variant="light" />
        )}
      </NavLink>

      {/* Nav */}
      <nav
        className={clsx(
          "flex-1 overflow-y-auto py-4",
          collapsed ? "px-2" : "px-3",
        )}
      >
        {visibleSections.map((sec, i) => (
          <div key={i} className={clsx(i > 0 && "mt-6")}>
            {sec.title && !collapsed && (
              <div className="mb-2 px-2 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                {sec.title}
              </div>
            )}
            {sec.title && collapsed && i > 0 && (
              <div className="mb-2 mx-2 border-t border-white/5" />
            )}
            <ul className="space-y-1">
              {sec.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      clsx(
                        "flex items-center rounded-lg text-sm font-medium transition-colors",
                        collapsed
                          ? "justify-center px-2 py-2.5"
                          : "gap-3 px-3 py-2",
                        isActive
                          ? "bg-cyan-400/10 text-cyan-300 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.15)]"
                          : "text-slate-400 dark:text-slate-500 hover:bg-white/5 hover:text-slate-100",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 flex-none" />
                    {!collapsed && item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Botão de toggle collapse (só desktop — passa via prop) */}
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={clsx(
            "flex items-center gap-2 border-t border-white/10 px-3 py-2 text-xs",
            "text-slate-500 hover:bg-white/5 hover:text-slate-200 transition-colors",
            collapsed && "justify-center",
          )}
          title={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>Recolher</span>
            </>
          )}
        </button>
      )}

      {/* Footer com contexto + LAIT — escondido em modo collapsed */}
      {!collapsed && (
        <div className="space-y-1.5 border-t border-white/10 px-4 py-3">
          <div className="text-xs text-slate-400 dark:text-slate-500">
            {auth.user?.role === "super_admin" ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                staff envpulse
              </span>
            ) : (
              <span>
                tenant:{" "}
                <span className="font-medium text-slate-200">
                  {auth.user?.tenant_id?.slice(0, 8) ?? "—"}
                </span>
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">
            uma empresa{" "}
            <span className="font-semibold text-slate-300">LAIT</span>
          </div>
        </div>
      )}
    </div>
  );
}
